import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  IconLoader,
  InputNumber,
  InputSize,
  InputText,
  PermissionEnum,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import InfoRow from '../../common/info-row';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import StepPartBlock from '../../common/step-part-block';
import { ProfessionalInformationTypes } from './types';

import commonStyles from '../../common/styles.module.css';

const ProfessionalInformation = ({
  control,
  clearErrors,
  errors,
  activePermissions,
  status,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  countries,
  clientsActivities,
  employmentActivity,
  clientActivity,
  otherActivity,
  businessOwnerActivity,
  questionnarieIsLoading,
}: ProfessionalInformationTypes) => (
  <StepPartBlock title="Professional information" zIndex={28}>
    {questionnarieIsLoading && (
      <div className={commonStyles.dictionaryLoader}>
        <IconLoader />
      </div>
    )}
    {!questionnarieIsLoading && (
      <>
        <InfoRow
          title="Activity of the client"
          id="clientActivityId"
          lessPadding
        >
          <Controller
            control={control}
            name="clientActivityId"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                name="clientActivityId"
                placeholder="Select an option"
                list={clientsActivities}
                value={value}
                selectWrapperClassName={
                  !activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  ) ||
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED
                    ? commonStyles.selectDisabled
                    : ''
                }
                size={SelectSize.SMALL}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['clientActivityId']);
                }}
                error={errors?.clientActivityId?.message}
                tabIndex={
                  !activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  ) ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  ) ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                fullWidth
                borderless
              />
            )}
          />
        </InfoRow>
        {employmentActivity?.value === clientActivity && (
          <>
            <InfoRow title="Name of employer" id="nameOfEmployer" lessPadding>
              <Controller
                control={control}
                name="nameOfEmployer"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="nameOfEmployer"
                    placeholder="Enter name of employer"
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['nameOfEmployer']);
                    }}
                    error={errors?.nameOfEmployer?.message}
                    size={InputSize.SMALL}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
            <InfoRow
              title="Nature of business activity"
              id="natureOfBusinessActivity"
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="natureOfBusinessActivity"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    name={name}
                    placeholder="Enter description..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['natureOfBusinessActivity']);
                    }}
                    size={InputSize.SMALL}
                    error={
                      questionnarieErrors?.natureOfBusinessActivity?.message
                    }
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
            <InfoRow title="Position" id="employeePosition" lessPadding>
              <Controller
                control={control}
                name="employeePosition"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="employeePosition"
                    placeholder="Enter position"
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['employeePosition']);
                    }}
                    size={InputSize.SMALL}
                    error={errors?.employeePosition?.message}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
            <InfoRow
              title="Country of Employment"
              id="countryOfEmployerId"
              lessPadding
            >
              <Controller
                control={control}
                name="countryOfEmployerId"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    name="countryOfEmployerId"
                    placeholder="Select country..."
                    list={countries || []}
                    value={value}
                    className={`${commonStyles.newApplicationAutocomplete} ${
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? commonStyles.newApplicationAutocompleteDisabled
                        : ''
                    }`}
                    error={errors?.countryOfEmployerId?.message}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['countryOfEmployerId']);
                    }}
                    borderless
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    fullWidth
                  />
                )}
              />
            </InfoRow>
          </>
        )}
        {clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value === clientActivity && (
          <InfoRow
            title="Nature of business activity"
            id="natureOfBusinessActivity"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="natureOfBusinessActivity"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter description..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['natureOfBusinessActivity']);
                  }}
                  size={InputSize.SMALL}
                  error={questionnarieErrors?.natureOfBusinessActivity?.message}
                  className="borderless"
                  tabIndex={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
        {otherActivity?.value === clientActivity && (
          <InfoRow
            title="Description of activity"
            id="typeOfActivityDescription"
            className={commonStyles.noFlex}
            lessPadding
          >
            <Controller
              control={control}
              name="typeOfActivityDescription"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Textarea
                  name="typeOfActivityDescription"
                  placeholder="Enter description..."
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['typeOfActivityDescription']);
                  }}
                  value={value}
                  className={`borderless ${commonStyles.stepOneTextarea} ${commonStyles.pepTextarea}`}
                  error={errors?.typeOfActivityDescription?.message}
                  tabIndex={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
        {businessOwnerActivity?.value === clientActivity && (
          <>
            <InfoRow title="Name of the Company" id="nameOfCompany" lessPadding>
              <Controller
                control={questionnarieControl}
                name="nameOfCompany"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    name={name}
                    placeholder="Enter name..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['nameOfCompany']);
                    }}
                    size={InputSize.SMALL}
                    error={questionnarieErrors?.nameOfCompany?.message}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
            <InfoRow title="Ownership in %" id="ownership" lessPadding>
              <Controller
                control={questionnarieControl}
                name="ownership"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <InputNumber
                    name={name}
                    placeholder="Enter percent..."
                    value={value}
                    onChange={(e) => {
                      let ownershipValue = e.target.value;

                      if (Number(e.target.value) > 100) {
                        ownershipValue = '100';
                      } else if (Number(e.target.value) < 0) {
                        ownershipValue = '0';
                      }

                      onChange(ownershipValue);
                      questionnarieClearErrors(['ownership']);
                    }}
                    size={InputSize.SMALL}
                    error={questionnarieErrors?.ownership?.message}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
            <InfoRow
              title="Country of Employment"
              id="countryOfEmployerId"
              lessPadding
            >
              <Controller
                control={control}
                name="countryOfEmployerId"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    name="countryOfEmployerId"
                    placeholder="Select country..."
                    list={countries || []}
                    value={value}
                    className={`${commonStyles.newApplicationAutocomplete} ${
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? commonStyles.newApplicationAutocompleteDisabled
                        : ''
                    }`}
                    error={errors?.countryOfEmployerId?.message}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['countryOfEmployerId']);
                    }}
                    borderless
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    fullWidth
                  />
                )}
              />
            </InfoRow>
            <InfoRow
              title="Nature of business activity"
              id="natureOfBusinessActivity"
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="natureOfBusinessActivity"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    name={name}
                    placeholder="Enter description..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['natureOfBusinessActivity']);
                    }}
                    size={InputSize.SMALL}
                    error={
                      questionnarieErrors?.natureOfBusinessActivity?.message
                    }
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          </>
        )}
      </>
    )}
  </StepPartBlock>
);

export default ProfessionalInformation;
